'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { ClinicPage } from '@borradh-workspace/api-client/types';
import { queryOptions, useQuery } from '@tanstack/react-query';

/**
 * A new clinic's setup page: which step they're on (contract, payment, form)
 * and what that step needs. The token is the credential, so it only ever
 * travels in the path. `sessionId` is Stripe's, after the embedded checkout.
 */
export const getClinicPageQueryOptions = (
  token: string,
  sessionId: string | null
) =>
  queryOptions({
    queryKey: queryKeys.clinicPage(token, sessionId),
    queryFn: () =>
      apiClient.get<ClinicPage>(
        `crm/clinic/${encodeURIComponent(token)}${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''}`
      ),
    enabled: !!token,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

export const useGetClinicPage = (token: string, sessionId: string | null) => {
  const query = useQuery(getClinicPageQueryOptions(token, sessionId));
  return {
    page: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
